import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, RefreshCw, LogOut, ArrowLeft, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "petraits_studio_token";

const SIZE_LABELS = {
  "9x12": '9" × 12" — The Keepsake ($100)',
  "14x18": '14" × 18" — The Portrait ($175)',
  "26x30": '26" × 30" — The Masterpiece ($350)',
  undecided: "Not sure yet",
};

const inputCls = "w-full px-4 py-3 rounded-xl bg-[#0C0A09] border border-[#2E2721] text-stone-200 text-sm placeholder:text-stone-600";

export const Studio = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [passcode, setPasscode] = useState("");
  const [requests, setRequests] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (t) => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/studio/requests`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setRequests(data.requests);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
        setRequests(null);
      } else {
        toast.error("Could not load requests", { description: "Please try again." });
      }
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/studio/login`, { passcode });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPasscode("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Could not sign in", { description: typeof detail === "string" ? detail : "Please try again." });
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setRequests(null);
  };

  return (
    <div data-testid="studio-page" className="min-h-screen bg-[#0C0A09] text-[#FAF6F0] font-sans">
      <div className="grain-overlay" />
      <header className="border-b border-stone-800/60 backdrop-blur-xl bg-[#0C0A09]/80 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="PETRAITS by Bash & Bird" className="h-10 w-10 rounded-full" />
            <span className="text-xs uppercase tracking-[0.25em] font-mono text-stone-500">Studio Inbox</span>
          </div>
          <div className="flex items-center gap-4">
            <a data-testid="studio-back-link" href="/" className="inline-flex items-center gap-2 text-xs text-stone-500 hover:text-[#41C2F2] transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to site
            </a>
            {token && (
              <button data-testid="studio-logout-button" onClick={logout} className="inline-flex items-center gap-2 text-xs text-stone-500 hover:text-[#C85A32] transition-colors">
                <LogOut className="w-3.5 h-3.5" /> Sign out
              </button>
            )}
          </div>
        </div>
      </header>

      {!token ? (
        <div className="flex items-center justify-center px-5 py-28">
          <form data-testid="studio-login-form" onSubmit={login} className="w-full max-w-sm p-8 rounded-2xl bg-[#1F1B17] border border-[#2E2721]">
            <img src="/logo.png" alt="PETRAITS by Bash & Bird" className="w-16 h-16 rounded-full" />
            <h1 className="mt-4 font-serif text-2xl text-[#EAF5FA]">Studio access</h1>
            <p className="mt-2 text-sm text-stone-400">Enter your passcode to view consultation requests.</p>
            <input
              data-testid="studio-passcode-input"
              type="password"
              required
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              placeholder="Passcode"
              className={`${inputCls} mt-6`}
              maxLength={100}
            />
            <button
              data-testid="studio-login-button"
              type="submit"
              disabled={busy}
              className="mt-5 w-full py-3.5 rounded-full bg-[#41C2F2] text-[#0C0A09] font-semibold text-sm tracking-wide hover:bg-[#BDE9FA] disabled:opacity-60 transition-colors duration-300"
            >
              {busy ? "Checking…" : "Open Inbox"}
            </button>
          </form>
        </div>
      ) : (
        <main className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
          <div className="flex items-center justify-between mb-8">
            <h1 className="font-serif text-3xl text-[#EAF5FA]">
              Consultation requests{requests ? ` · ${requests.length}` : ""}
            </h1>
            <button
              data-testid="studio-refresh-button"
              onClick={() => load(token)}
              disabled={busy}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-stone-700 text-stone-300 text-xs hover:border-[#41C2F2] hover:text-[#41C2F2] transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>

          {requests === null ? (
            <p className="text-stone-500 text-sm">Loading…</p>
          ) : requests.length === 0 ? (
            <div data-testid="studio-empty-state" className="p-12 rounded-2xl bg-[#1F1B17] border border-[#2E2721] text-center">
              <p className="font-serif text-xl text-stone-300">No requests yet</p>
              <p className="mt-2 text-sm text-stone-500">New consultation requests will appear here the moment they arrive.</p>
            </div>
          ) : (
            <div className="space-y-5">
              {requests.map((r, i) => (
                <article key={r.created_at + String(i)} data-testid={`studio-request-card-${i + 1}`} className="p-6 sm:p-7 rounded-2xl bg-[#1F1B17] border border-[#2E2721]">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h2 className="font-serif text-xl text-[#EAF5FA]">{r.name}</h2>
                      <a href={`mailto:${r.email}`} className="inline-flex items-center gap-2 text-sm text-stone-400 hover:text-[#41C2F2] transition-colors mt-1">
                        <Mail className="w-3.5 h-3.5" /> {r.email}
                      </a>
                    </div>
                    <p className="text-xs font-mono text-stone-500">
                      {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                    </p>
                  </div>
                  <dl className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                    <div><dt className="text-xs uppercase tracking-[0.2em] font-mono text-stone-500">Pet(s)</dt><dd className="text-stone-200 mt-1">{r.pet_name || "—"}</dd></div>
                    <div><dt className="text-xs uppercase tracking-[0.2em] font-mono text-stone-500">Size</dt><dd className="text-stone-200 mt-1">{SIZE_LABELS[r.size] || r.size}</dd></div>
                    <div><dt className="text-xs uppercase tracking-[0.2em] font-mono text-stone-500">Background</dt><dd className="text-stone-200 mt-1">{r.background || "—"}</dd></div>
                    <div><dt className="text-xs uppercase tracking-[0.2em] font-mono text-stone-500">Accents</dt><dd className="text-stone-200 mt-1">{r.accents || "—"}</dd></div>
                  </dl>
                  {r.notes && (
                    <p className="mt-5 text-sm text-stone-300 leading-relaxed border-l border-[#41C2F2]/30 pl-4">{r.notes}</p>
                  )}
                  {r.photo_paths?.length > 0 && (
                    <div className="mt-5 flex flex-wrap gap-3">
                      {r.photo_paths.map((p, j) => (
                        <a key={p} data-testid={`studio-request-${i + 1}-photo-${j + 1}`} href={`${API}/files/${p}`} target="_blank" rel="noopener noreferrer" className="block w-20 h-20 rounded-lg overflow-hidden border border-[#2E2721] hover:border-[#41C2F2] transition-colors">
                          <img src={`${API}/files/${p}`} alt={`Pet photo ${j + 1}`} className="w-full h-full object-cover" loading="lazy" />
                        </a>
                      ))}
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}
        </main>
      )}
    </div>
  );
};
